import { ExperimentSegments, getExperimentSegment } from '@ratehub/base-ui';

import EXPERIMENTS from '../definitions/Experiments';
import APPROVAL_LIKELIHOOD from '../definitions/ApprovalLikelihood';
import { APPROVAL_LIKELIHOOD_SCORES, PRODUCT_TYPES } from '../definitions/CurrentDocument';
import getApprovalLikelihoodMinimums from './getApprovalLikelihoodMinimums';


/**
 * Maps a raw API approval likelihood value (as found in API response card.approval_likelihood_raw)
 * into an entry within APPROVAL_LIKELIHOOD (strings from 'unknown' through 'prequalified').
 * @param {boolean} isVerified - has the rawLikelihood value been verified from a valid approval likelihood document?
 * @param {number} rawLikelihood - a float from 0..100, or null
 * @param {string[]} [cardTypes=[]] - an array of CC product types for the card in question
 * @returns {string} a value from APPROVAL_LIKELIHOOD, or undefined if rawLikelihood is undefined
 */
function getApprovalLikelihoodFromAPIResponse(
    isVerified,
    rawLikelihood,
    cardTypes = []
) {
    if (!isVerified) {
        return undefined;
    }

    // null or undefined rawLikelihood means unknown
    if (rawLikelihood == null) {
        return APPROVAL_LIKELIHOOD.UNKNOWN;
    }

    // secured cards with ANY rawLikelihood (expecting only 100, btw)
    // should always be considered GOOD approval likelihood
    if (cardTypes.includes(PRODUCT_TYPES.SECURED)) {
        return APPROVAL_LIKELIHOOD.GOOD;
    }

    const minimumThresholds = getApprovalLikelihoodMinimums(
        getExperimentSegment(EXPERIMENTS.approvalThreshold) ?? ExperimentSegments.CONTROL);

    if (rawLikelihood === 100) {
        return APPROVAL_LIKELIHOOD_SCORES.PREQUALIFIED;
    } else if (rawLikelihood >= minimumThresholds.excellent) {
        return APPROVAL_LIKELIHOOD_SCORES.EXCELLENT;
    } else if (
        minimumThresholds.veryGood != null // only present in variants
        && rawLikelihood >= minimumThresholds.veryGood
    ) {
        return APPROVAL_LIKELIHOOD_SCORES.VERY_GOOD;
    } else if (minimumThresholds != null && rawLikelihood >= minimumThresholds.good) {
        return APPROVAL_LIKELIHOOD_SCORES.GOOD;
    } else if (rawLikelihood >= minimumThresholds.fair) {
        return APPROVAL_LIKELIHOOD_SCORES.FAIR;
    } else {
        return APPROVAL_LIKELIHOOD_SCORES.POOR;
    }
}

const APPROVAL_LIKELIHOOD_RANKING = [
    APPROVAL_LIKELIHOOD.UNKNOWN,
    APPROVAL_LIKELIHOOD.POOR,
    APPROVAL_LIKELIHOOD.FAIR,
    APPROVAL_LIKELIHOOD.GOOD,
    APPROVAL_LIKELIHOOD.VERY_GOOD,
    APPROVAL_LIKELIHOOD.EXCELLENT,
    APPROVAL_LIKELIHOOD.PREQUALIFIED,
];

/**
 * Maps an cardInfo APPROVAL_LIKELIHOOD value into a rank number where higher is more likely to be approved
 * @param {string} approvalLikelihood - APPROVAL_LIKELIHOOD value
 * @returns {number} index value of APPROVAL_LIKELIHOOD
 */
function getApprovalLikelihoodRank(approvalLikelihood) {
    return APPROVAL_LIKELIHOOD_RANKING.indexOf(approvalLikelihood);
}


export { getApprovalLikelihoodRank };
export default getApprovalLikelihoodFromAPIResponse;
